import { IconButton, iconSizes, toast } from '@common/ui'
import { IconMaximize, IconMinimize, IconRefresh } from '@common/ui/icons'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { com } from '@eidu/web'
import EntityWithLabelAndRelated from '../../../../domain/entity/EntityWithLabelAndRelated'
import { getKnownKindKey } from '../../../../domain/kindRegistry'
import containerSrc from '../../../../../runtime-packages/runtime-container/dist/container.js?raw'
import { useFetchEntity } from '../../../../io/useEntities'
import listVersions, { VersionRecord } from '../../../../io/api/entity/data/listVersions'
import fetchVersionSnapshot from '../../../../io/api/entity/data/fetchVersionSnapshot'
import getRegistryAsset from '../../../../io/api/registry/getRegistryAsset'
import snapshotEntityToEntity from '../../../../io/snapshotEntityToEntity'
import Entity = com.eidu.entity.Entity
import EntityId = com.eidu.entity.EntityId
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import entityIdFromStringOrNull = com.eidu.entity.entityIdFromStringOrNull
import EntitySnapshot = com.eidu.entity.publish.EntitySnapshot
import SnapshotEntity = com.eidu.entity.publish.SnapshotEntity
import type { RuntimeHost, RuntimeTheme, ValidationResult } from 'runtimes-shared'
import fetchService from '../../../../io/api/fetchService'
import getEntities from '../../../../io/api/entity/getEntities'
import getEntitiesById from '../../../../io/api/entity/getEntitiesById'
import requireAuthContext from '../../../../io/api/authorization/requireAuthContext'
import postTrackingEvent from '../../../../io/api/event/tracking/postTrackingEvent'
import getLocation from './getLocation'
import { createRuntimeHostTracing, type RuntimeHostTracing } from '../../../../io/tracing/runtimeHostTracing'
import { attachRuntimeErrorReporting, captureRuntimeError } from './runtimeErrorReporting'

const srcDoc = `<html><body><script>${containerSrc}</script></body></html>`

const resolvePrimaryColor = (): string =>
  (typeof window !== 'undefined' &&
    getComputedStyle(document.documentElement).getPropertyValue('--trellis-color-primary').trim()) ||
  '#339bdc'

// Resolves an asset's published versions whether or not the asset belongs to the current org. The registry endpoint
// is org-agnostic for any asset that has been registered, so we try that first and fall back to the org-scoped
// /entities/{id}/versions endpoint for unregistered same-org assets.
const fetchVersionsAnyOrg = async (id: EntityId): Promise<VersionRecord[]> => {
  try {
    return (await getRegistryAsset(id.toString())).versions
  } catch {
    return await listVersions({ id })
  }
}

const waitForRuntimeHost = (win: Window & { runtimeHost?: RuntimeHost }): Promise<RuntimeHost> => {
  if (win.runtimeHost) return Promise.resolve(win.runtimeHost)
  return new Promise((resolve, reject) => {
    let attempts = 0
    const interval = setInterval(() => {
      if (win.runtimeHost) {
        clearInterval(interval)
        resolve(win.runtimeHost)
      } else if (++attempts > 50) {
        clearInterval(interval)
        reject(new Error('runtimeHost not available after timeout'))
      }
    }, 50)
  })
}

export const useLocalRuntimes = import.meta.env.VITE_LOCAL_RUNTIMES === 'true'

type ClosureIndex = {
  inline: Map<string, SnapshotEntity>
}

const indexClosure = (snapshot: EntitySnapshot): ClosureIndex => {
  const inline = new Map<string, SnapshotEntity>()
  for (const [entityId, snapshotEntity] of snapshot.inlineEntities.asJsReadonlyMapView().entries())
    inline.set(entityId.toString(), snapshotEntity)

  return { inline }
}

export const AssetPreviewView = ({
  entity,
  entityKind,
  entityTypes,
  pinnedVersionId,
  onValidationChange,
  onRefresh,
  onLog,
}: {
  entity: EntityWithLabelAndRelated
  entityKind?: string
  entityTypes?: ReadonlyMap<EntityTypeId, EntityType>
  pinnedVersionId?: string
  onValidationChange?: (result: ValidationResult) => void
  onRefresh?: () => void
  onLog?: (level: 'log' | 'warn' | 'error', args: unknown[]) => void
}) => {
  const runtimeTheme: RuntimeTheme = useMemo(() => ({ primaryColor: resolvePrimaryColor() }), [])
  const containerRef = useRef<HTMLDivElement>(null)
  const containerWindowRef = useRef<(Window & { runtimeHost: RuntimeHost }) | null>(null)
  const hostTracingRef = useRef<RuntimeHostTracing | null>(null)
  const [fullscreen, setFullscreen] = useState(false)
  const [iframeKey, setIframeKey] = useState(0)

  const fetchById = useFetchEntity()

  const entityId = entity.entity.id

  const versionsQuery = useQuery({
    queryKey: ['versions', entityId.toString()],
    queryFn: () => fetchVersionsAnyOrg(entityId),
    enabled: !!pinnedVersionId,
  })
  const pinnedRecord = pinnedVersionId ? versionsQuery.data?.find((r) => r.versionId === pinnedVersionId) : undefined

  const snapshotQuery = useQuery({
    queryKey: ['versionSnapshot', pinnedRecord?.snapshotResourceKey],
    queryFn: () => fetchVersionSnapshot(pinnedRecord!.snapshotResourceKey),
    enabled: !!pinnedRecord,
  })
  const snapshot = pinnedVersionId ? snapshotQuery.data : undefined

  const closureIndex = useMemo(() => (snapshot ? indexClosure(snapshot) : undefined), [snapshot])

  useEffect(() => {
    const handler = () => setFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  useEffect(() => () => hostTracingRef.current?.end(), [])

  // Prefer the Fullscreen API so desktop gets real chrome-hiding fullscreen.
  // iOS Safari does not implement requestFullscreen() on non-video elements,
  // so we fall back to a CSS overlay there.
  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    if (typeof el.requestFullscreen === 'function') {
      if (document.fullscreenElement) document.exitFullscreen()
      else el.requestFullscreen()
    } else {
      setFullscreen((f) => !f)
    }
  }, [])

  const runRuntimeHost = useCallback(
    async (runtimeHost: RuntimeHost) => {
      // Pool of entities discovered via the root snapshot or any versioned dependency we resolve at runtime. We
      // merge every fetched snapshot's inlineEntities here so subsequent unversioned id lookups by the runtime
      // (including cross-org transitive deps the running fetcher cannot see) resolve from the pool.
      const inlinePool = new Map<string, SnapshotEntity>()
      if (closureIndex) for (const [k, v] of closureIndex.inline.entries()) inlinePool.set(k, v)
      if (snapshot) inlinePool.set(snapshot.entity.entityId.toString(), snapshot.entity)

      const ingestSnapshot = (s: EntitySnapshot) => {
        inlinePool.set(s.entity.entityId.toString(), s.entity)
        for (const [k, v] of s.inlineEntities.asJsReadonlyMapView().entries()) inlinePool.set(k.toString(), v)
      }

      const getEntityJson = async (id: string, versionId?: string): Promise<string> => {
        const eid = entityIdFromStringOrNull(id)
        if (!eid) throw new Error(`Invalid entity id: ${id}`)

        if (versionId) {
          const records = await fetchVersionsAnyOrg(eid)
          const record = records.find((r) => r.versionId === versionId)
          if (!record) throw new Error(`No version ${versionId} for entity ${id}`)
          const nestedSnapshot = await fetchVersionSnapshot(record.snapshotResourceKey)
          ingestSnapshot(nestedSnapshot)
          return Entity.toJsonString(snapshotEntityToEntity(nestedSnapshot.entity))
        }

        const pooled = inlinePool.get(id)
        if (pooled) return Entity.toJsonString(snapshotEntityToEntity(pooled))

        const live = await fetchById(eid)
        if (!live) throw new Error(`Entity not found: ${id}`)
        return Entity.toJsonString(live.entity)
      }

      const rootEntityJson = snapshot
        ? Entity.toJsonString(snapshotEntityToEntity(snapshot.entity))
        : Entity.toJsonString(entity.entity)

      const auth = await requireAuthContext()

      // Session root span for this preview — the runtime's load/render and API calls merge under it.
      // The `eidu.runtime.*` keys are host-neutral and shared with the Field App's WebView host so one
      // trace query spans both. `config.version` is the pinned published version, or "live" for an
      // unpinned draft preview.
      const entryLabel = snapshot?.entity.primaryLabel ?? entity.primaryLabelText
      hostTracingRef.current?.end()
      const hostTracing = createRuntimeHostTracing(
        'AssetPreview.session',
        {
          'eidu.runtime.entry_entity_id': entity.entity.id.toString(),
          'eidu.config.version': pinnedVersionId ?? 'live',
          ...(entryLabel.trim() ? { 'eidu.runtime.entry_entity_label': entryLabel } : {}),
        },
        fetchService
      )
      hostTracingRef.current = hostTracing

      runtimeHost.run({
        entityJson: rootEntityJson,
        user: { entityId: auth.userEntityId, organizationId: auth.organizationId, syncUserId: auth.syncUserId },
        getEntityJson: (id, versionId, parent) => hostTracing.withinParent(parent, () => getEntityJson(id, versionId)),
        fetchService: hostTracing.fetchService,
        tracing: hostTracing.tracing,
        onLoad: () => {
          onValidationChange?.(runtimeHost.validate())
        },
        onLog,
        openAsset: (openId: string) => {
          const eid = entityIdFromStringOrNull(openId)
          if (!eid) {
            toast.negative(`Invalid entity id: ${openId}`)
            return
          }
          fetchById(eid)
            .then((e) => {
              if (!e) throw new Error(`Entity not found: ${openId}`)
              window.open(`/entities/${e.entity.typeId}/view/${openId}`, '_blank')
            })
            .catch((err) => {
              console.error('[AssetPreview] openAsset failed:', err)
              toast.negative(`Could not open entity ${openId}`)
            })
        },
        postTrackingEvent: (
          category: string,
          action: string,
          payload?: Record<string, unknown>,
          assetId?: string,
          parent?: string
        ): Promise<void> =>
          hostTracing.withinParent(parent, () =>
            postTrackingEvent(category, action, payload ?? {}, assetId ?? entity.entity.id.toString())
          ),
        theme: runtimeTheme,
        getLocation: (coarse, parent) => hostTracing.withinParent(parent, () => getLocation(coarse)),
        getEntities: (typeId, opts, parent) => hostTracing.withinParent(parent, () => getEntities(typeId, opts)),
        getEntitiesById,
      })
    },
    [entity, fetchById, closureIndex, snapshot, pinnedVersionId]
  )

  useEffect(() => {
    setIframeKey((k) => k + 1)
  }, [pinnedVersionId, snapshot])

  // Report runtime errors escaping the preview iframe to Sentry (see runtimeErrorReporting.ts). Context
  // mirrors the Field App's correlation so a Sentry event ties back to the trace, config version, entry entity.
  const reportRuntimeError = useCallback(
    (error: unknown) => {
      const traceparent = hostTracingRef.current?.tracing.rootTraceparent
      captureRuntimeError(error, {
        entryEntityId: entity.entity.id.toString(),
        configVersion: pinnedVersionId ?? 'live',
        traceId: traceparent?.split('-')[1] ?? null,
      })
    },
    [entity, pinnedVersionId]
  )

  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLIFrameElement>) => {
      const win = e.currentTarget.contentWindow as (Window & { runtimeHost: RuntimeHost }) | null
      if (!win) return
      containerWindowRef.current = win
      attachRuntimeErrorReporting(win, reportRuntimeError)
      if (useLocalRuntimes && entityTypes) {
        const typeIdToKind: Record<string, string> = {}
        for (const [id, type] of entityTypes) {
          const kind = getKnownKindKey(type.kind)
          if (kind) typeIdToKind[id.toString()] = kind
        }
        win.__typeIdToKind = typeIdToKind
        // Module scripts in host.html may execute after the iframe load event
        waitForRuntimeHost(win).then(runRuntimeHost)
      } else {
        runRuntimeHost(win.runtimeHost)
      }
    },
    [entity, fetchById, entityTypes, runRuntimeHost, reportRuntimeError]
  )

  // Defer mounting the preview iframe until the snapshot is ready when pinned —
  // otherwise the first render would use the live entity and flash before the pinned one loads.
  const awaitingSnapshot = !!pinnedVersionId && !snapshot

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        ...(fullscreen && {
          position: 'fixed' as const,
          inset: 0,
          zIndex: 200,
          backgroundColor: 'var(--trellis-color-surface)',
        }),
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton
          aria-label="Reload preview"
          onClick={() => {
            onRefresh?.()
            setIframeKey((k) => k + 1)
          }}
        >
          <IconRefresh size={iconSizes.sm} stroke={1.5} />
        </IconButton>
        <IconButton aria-label={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'} onClick={toggleFullscreen}>
          {fullscreen ? (
            <IconMinimize size={iconSizes.sm} stroke={1.5} />
          ) : (
            <IconMaximize size={iconSizes.sm} stroke={1.5} />
          )}
        </IconButton>
      </div>
      <div
        style={{
          border: '1px solid var(--trellis-color-outline)',
          borderRadius: 'var(--trellis-radius-xs)',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {awaitingSnapshot ? null : useLocalRuntimes && entityKind ? (
          <iframe
            key={iframeKey}
            title="Asset preview"
            src="/runtime-packages/runtime-container/host.html"
            onLoad={handleLoad}
            style={{ flex: 1, width: '100%', minHeight: 200, border: 'none' }}
          />
        ) : (
          <iframe
            key={iframeKey}
            title="Asset preview"
            srcDoc={srcDoc}
            sandbox="allow-scripts allow-same-origin"
            onLoad={handleLoad}
            style={{ flex: 1, width: '100%', minHeight: 200, border: 'none' }}
          />
        )}
      </div>
    </div>
  )
}
